/**
 * BdcPhone — call log page for the BDC portal.
 *
 * Route: /bdc/phone
 * - Filters: direction, status, keyword search
 * - Shows: contact / lead / status / duration / recording playback / transcript
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail,
  Search, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PageContainer, PageHeader, StatusPill, Skeleton, relativeTime, formatDateTime,
} from "./bdcUi";
import { formatPhoneDisplay, formatDuration } from "@/lib/phone";
import { useSoftphone, SOFTPHONE_STATUS } from "@/context/SoftphoneContext";
import AiCallSummary from "@/components/AiCallSummary";

const STATUS_META = {
  "completed": { label: "Completed", tone: "emerald" },
  "in-progress": { label: "In progress", tone: "blue" },
  "ringing": { label: "Ringing", tone: "amber" },
  "initiated": { label: "Placed", tone: "slate" },
  "queued": { label: "Queued", tone: "slate" },
  "no-answer": { label: "No answer", tone: "rose" },
  "busy": { label: "Busy", tone: "rose" },
  "failed": { label: "Failed", tone: "rose" },
  "canceled": { label: "Canceled", tone: "slate" },
  "voicemail": { label: "Voicemail", tone: "amber" },
};

const DirectionIcon = ({ direction, status }) => {
  if (status === "voicemail") return <Voicemail size={14} className="text-amber-600" />;
  if (["no-answer", "busy", "failed"].includes(status)) {
    return <PhoneMissed size={14} className="text-rose-600" />;
  }
  if (direction === "outbound") return <PhoneOutgoing size={14} className="text-slate-500" />;
  return <PhoneIncoming size={14} className="text-slate-500" />;
};

const BdcPhone = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [direction, setDirection] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const softphone = useSoftphone();

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (direction !== "all") p.direction = direction;
    if (status !== "all") p.status = status;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [page, pageSize, direction, status, q]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/bdc/calls", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [params]);

  const callBack = (num) => {
    if (softphone.status !== SOFTPHONE_STATUS.READY) {
      toast.error("Softphone must be online to dial. Toggle 'Go online' first.");
      return;
    }
    softphone.dial(num);
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <PageContainer>
      <PageHeader
        title="Phone calls"
        subtitle={`${total} total · inbound + outbound + voicemail`}
        testId="bdc-phone-page-header"
      />

      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="bdc-phone-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
            placeholder="Search phone number or transcript…"
            className="pl-8"
            data-testid="bdc-phone-search-input"
          />
        </div>
        <Select value={direction} onValueChange={(v) => { setPage(1); setDirection(v); }}>
          <SelectTrigger className="w-36" data-testid="bdc-phone-direction-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All directions</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="outbound">Outbound</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
          <SelectTrigger className="w-40" data-testid="bdc-phone-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="voicemail">Voicemail</SelectItem>
            <SelectItem value="no-answer">No answer</SelectItem>
            <SelectItem value="busy">Busy</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="bdc-phone-list">
          {loading ? (
            <div className="p-4 space-y-2">{[0,1,2,3].map((i) => <Skeleton key={i} className="h-14" />)}</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">No calls match these filters yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((c) => {
                const meta = STATUS_META[c.status] || STATUS_META.initiated;
                const peer = c.direction === "outbound" ? c.to_number : c.from_number;
                return (
                  <li
                    key={c.id}
                    className={`p-3 sm:p-4 hover:bg-slate-50 cursor-pointer flex items-start gap-3 ${selected?.id === c.id ? "bg-slate-50" : ""}`}
                    onClick={() => setSelected(c)}
                    data-testid={`bdc-phone-row-${c.id}`}
                  >
                    <div className="mt-1"><DirectionIcon direction={c.direction} status={c.status} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 text-sm">
                          {c.lead_label || formatPhoneDisplay(peer)}
                        </span>
                        {c.lead_label && (
                          <span className="text-xs text-slate-400 font-mono">{formatPhoneDisplay(peer)}</span>
                        )}
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {relativeTime(c.started_at)} · {formatDateTime(c.started_at)}
                        {c.duration_seconds ? ` · ${formatDuration(c.duration_seconds)}` : ""}
                      </p>
                      {(c.ai_summary_status === "completed" && c.ai_summary) ? (
                        <div className="mt-1">
                          <AiCallSummary call={c} variant="row" />
                        </div>
                      ) : c.transcript && (
                        <p className="text-xs text-slate-600 mt-1 line-clamp-1 italic">"{c.transcript.slice(0, 140)}"</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-brand hover:bg-brand/10 shrink-0"
                      onClick={(e) => { e.stopPropagation(); callBack(peer); }}
                      data-testid={`bdc-phone-callback-btn-${c.id}`}
                      title="Call back"
                    >
                      <PhoneCall size={14} />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          {pages > 1 && (
            <div className="p-3 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100">
              <span>Page {page} of {pages}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="bdc-phone-prev">Prev</Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="bdc-phone-next">Next</Button>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bdc-phone-detail-panel">
          {!selected ? (
            <div className="text-sm text-slate-500 text-center py-16">
              <Phone size={28} className="mx-auto text-slate-300 mb-2" />
              Select a call to see details, recording, and transcript.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    {selected.direction === "outbound" ? "Outbound" : "Inbound"} · {STATUS_META[selected.status]?.label || selected.status}
                  </p>
                  <p className="font-display text-xl font-semibold text-slate-900 mt-0.5">
                    {formatPhoneDisplay(selected.direction === "outbound" ? selected.to_number : selected.from_number)}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formatDateTime(selected.started_at)}{selected.duration_seconds ? ` · ${formatDuration(selected.duration_seconds)}` : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-brand hover:bg-brand/90 text-white"
                  onClick={() => callBack(selected.direction === "outbound" ? selected.to_number : selected.from_number)}
                  data-testid="bdc-phone-detail-callback-btn"
                >
                  <PhoneCall size={12} /> Call back
                </Button>
              </div>

              {selected.from_lead_id && (
                <Link
                  to={`/bdc/leads/${selected.from_lead_id}`}
                  className="text-xs text-brand hover:underline inline-flex items-center gap-1"
                  data-testid="bdc-phone-lead-link"
                >
                  Open lead <ExternalLink size={10} />
                </Link>
              )}

              {selected.recording_url && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Recording</p>
                  <audio
                    controls
                    src={selected.recording_url}
                    className="w-full"
                    data-testid="bdc-phone-recording-audio"
                  >
                    Your browser does not support audio playback.
                  </audio>
                </div>
              )}

              {(selected.ai_summary || ["generating","pending","failed"].includes(selected.ai_summary_status)) && (
                <div className="mt-4">
                  <AiCallSummary call={selected} variant="card" />
                </div>
              )}

              {(selected.transcript || selected.voicemail_transcript) && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">
                    {selected.status === "voicemail" ? "Voicemail transcript" : "Transcript"}
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-line bg-slate-50 rounded-lg p-3" data-testid="bdc-phone-transcript">
                    {selected.transcript || selected.voicemail_transcript}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default BdcPhone;
